const {
    filter,
    findLast,
    get,
    isEmpty,
    merge,
    replace,
    sortBy,
} = require('lodash');

const {
    PLAN_TYPE,
    SUBSCRIBER_TYPE,
} = require('../delivery-saver.constants');

const SUBSCRIPTION_PROCESSING_STATE_ID = 'SubscriptionProcessing';

/**
 * Delivery Saver Payment Setup Component.
 * Note 1: Avoid adding payment type specific concerns in this component.
 *         i.e. This component does not need to know about specific payment instrument types (e.g. giftCard or Paypal)
 *         All payment type specific logic should be part of the instruments component.
 */
class DeliverySaverPaymentSetupController {
    constructor(
        $element,
        $q,
        $state,
        $filter,
        $scope,
        settings,
        announcerService,
        digitalPayPaymentErrorDisplayService,
        loadingIndicatorService,
        digitalPayIframeEventHandler,
        digitalPayPaymentDistributionService,
        digitalPayPrepaymentActionsService,
        digitalPayDisablePaymentStateService,
        digitalPayPaymentErrorStateService,
        digitalPayErrorMessageGenerator,
        deliverySaverService,
        shopperService,
        dialogRegistry,
        analytics,
    ) {
        this.$element = $element;
        this.$q = $q;
        this.$state = $state;
        this.$filter = $filter;
        this.$scope = $scope;
        this.settings = settings;
        this.announcerService = announcerService;
        this.digitalPayPaymentErrorDisplayService = digitalPayPaymentErrorDisplayService;
        this.loadingIndicatorService = loadingIndicatorService;
        this.iframeEventHandler = digitalPayIframeEventHandler;
        this.paymentDistributionService = digitalPayPaymentDistributionService;
        this.prePaymentActionsService = digitalPayPrepaymentActionsService;
        this.disablePaymentStateService = digitalPayDisablePaymentStateService;
        this.digitalPayPaymentErrorStateService = digitalPayPaymentErrorStateService;
        this.digitalPayErrorMessageGenerator = digitalPayErrorMessageGenerator;
        this.deliverySaverService = deliverySaverService;
        this.shopperService = shopperService;
        this.dialogRegistry = dialogRegistry;
        this.analytics = analytics;
        this.SUBSCRIBER_TYPE = SUBSCRIBER_TYPE;
    }

    $onInit() {
        this.isInitialised = false;
        this.agreeToTncs = false;
        this.agreeToSubscriptionTncs = false;
        this.isCvvDialogEnabled = false;
        this.iframeEventHandler.init();
        this.prePaymentActionsService.init();
        this.disablePaymentStateService.init();
        this.digitalPayPaymentErrorStateService.init();
        this.digitalPayErrorTitle = 'Sorry, we\'ve encountered an issue.';

        this.prePaymentActionDefinitions = {
            addAction: this.prePaymentActionsService.addAction.bind(this.prePaymentActionsService),
            removeAction: this.prePaymentActionsService.removeAction.bind(this.prePaymentActionsService),
        };

        this.paymentStateDefinitions = {
            addDisablePaymentState: this.disablePaymentStateService.addDisablePaymentState.bind(this.disablePaymentStateService),
            removeDisablePaymentState: this.disablePaymentStateService.removeDisablePaymentState.bind(this.disablePaymentStateService),
        };

        const isMidweek = this.$state.params.planType === PLAN_TYPE.MID_WEEK;
        const isAnyDay = this.$state.params.planType === PLAN_TYPE.ANY_DAY;
        const planSubscriberType = this.$state.params.subscriberType;

        if (!(isMidweek || isAnyDay)) {
            // Invalid/missing query param
            this.restartSignupJourney();
        }

        this.deliverySaverService.listPlans().then((response) => {
            const plans = filter(response.Plans, p =>
                p.IsMidweek === isMidweek
                && p.IsAnyDay === isAnyDay
                && p.Metadata
                && p.Metadata.SubscriberType === planSubscriberType);

            this.plans = sortBy(plans, 'DisplayOrder');

            if (!this.plans.length) {
                this.restartSignupJourney();
                return;
            }

            this.changePlanSelection(this.plans[0]);

            this.isInitialised = true;
        });
    }

    changePlanSelection(selectedPlan) {
        this.selectedPlan = selectedPlan;

        const billingFrequency = this.$filter('deliverySaverBillingFrequency')(this.selectedPlan);
        const typeName = this.$filter('deliverySaverTypeName')(this.selectedPlan);
        const formatSetting = setting => this.formatSetting(billingFrequency, typeName, setting);

        if (this.selectedPlan.FreeTrialDays) {
            this.paymentSetupDetailText = formatSetting(this.settings.deliverySaver.freeTrialPaymentSetupDetailText);
            this.paymentSetupDisclaimerTextHtml = formatSetting(this.settings.deliverySaver.freeTrialPaymentSetupDisclaimerText);
        } else {
            this.paymentSetupDetailText = formatSetting(this.settings.deliverySaver.paymentSetupDetailText);
            this.paymentSetupDisclaimerTextHtml = formatSetting(this.settings.deliverySaver.paymentSetupDisclaimerText);
        }
    }

    formatSetting(billingFrequency, typeName, setting) {
        return setting.replace('{{ BillingFrequency }}', billingFrequency).replace('{{ TypeName }}', typeName);
    }

    restartSignupJourney() {
        this.$state.go('shop.deliveryunlimited');
    }

    $onDestroy() {
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
        this.iframeEventHandler.dispose();
        this.prePaymentActionsService.removeAllActions();
        this.digitalPayPaymentErrorStateService.clearErrors();
    }

    /**
     * Notifies when child instrument components are in manage mode.
     * @param {*boolean} state
     */
    notifyWhenChildIsInManageMode(state) {
        // The field here is an additional field used for showing the account password textbox
        this.anyChildIsInManageMode = state;
    }

    shouldDisablePayment() {
        return this.disablePaymentStateService.hasDisablePaymentStates()
            || !this.paymentDistributionService.hasSingleSelectType()
            || !this.agreeToTncs
            || !this.agreeToSubscriptionTncs
            || !this.billingAddress
            || !this.selectedPlan;
    }

    subscribeToDeliverySaver() {
        // logic from digital-pay-button
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();

        if (this.shouldDisablePayment()) {
            this.announcerService.announce(replace(this.settings.digitalPayScreenReaderMessages.placeOrderInvalidDisableMessage, '{0}', this.disablePaymentStateService.getReasonsWhyDisabled()));
            return;
        }

        this.paymentStateDefinitions.addDisablePaymentState(SUBSCRIPTION_PROCESSING_STATE_ID, this.settings.digitalPayScreenReaderMessages.subscriptionProcessingDisableMessage);
        this.resetPaymentStates();
        const prePaymentActions = this.prePaymentActionsService.getAllActions();

        const prePaymentPromises = [];
        prePaymentActions.forEach((action) => {
            prePaymentPromises.push(action());
        });

        this.loadingIndicatorService.startLoading(true, 'Processing your payment', false);

        // Once all prepayment promises are resolved make the payment.
        this.$q.all(prePaymentPromises)
            .then(prePaymentResults => this.getPaymentInstrument(prePaymentResults))
            .then(paymentInstrument => this.subscribePromise(paymentInstrument))
            .catch((error) => {
                this.hasPrePaymentActionFailures = true;
                if (error && error.errorMessage) {
                    this.digitalPayPaymentErrorDisplayService.pushGlobalError(error.errorMessage);
                }
            })
            .finally(() => {
                this.loadingIndicatorService.finishLoading();
                this.paymentStateDefinitions.removeDisablePaymentState(SUBSCRIPTION_PROCESSING_STATE_ID);
            });
    }

    subscribePromise(paymentInstrument) {
        return this.deliverySaverService
            .subscribe(this.selectedPlan.Id, paymentInstrument)
            .then((data) => {
                // Update shopperService.shopper.DeliveryPlus.Status
                this.shopperService.getCurrentShopperDetails();

                this.$state.go('shop.deliveryunlimited.confirmation', { data });
            })
            .catch(response => this.handleSubscribeFailure(response));
    }

    /**
     * Returns a paymentInstrument object
     * which is a merging of all the results for the instrument being used
     * @param {Promise[]} prePaymentResults
     * @returns {Object} paymentInstrument with paymentInstrumentId and stepUpToken
     */
    getPaymentInstrument(prePaymentResults) {
        let paymentInstrumentId = this.paymentDistributionService.getSingleSelectKey();
        if (this.paymentDistributionService.isPayingWithNewCard()) {
            // There should only be one prePaymentResult
            // but double check for a stepUpToken to ensure we use the right one
            paymentInstrumentId = get(findLast(prePaymentResults, result => result.paymentInstrumentId && result.stepUpToken), 'paymentInstrumentId');
        }
        if (paymentInstrumentId != null) {
            const paymentInstrumentResults = filter(prePaymentResults, result => result.paymentInstrumentId === paymentInstrumentId);
            const paymentInstrument = merge(...paymentInstrumentResults);
            if (!isEmpty(paymentInstrument)) {
                return paymentInstrument;
            }
        }
        return this.$q.reject({ errorMessage: this.settings.deliverySaver.paymentSetupGenericError });
    }

    /**
     * For clearing errors and resetting prepayment states
     */
    resetPaymentStates() {
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
        this.hasPrePaymentActionFailures = false;
        this.hasPrePaymentValidationFailure = false;
        this.shouldRemoveNewlyAddedInstruments = false;
        this.digitalPayErrorMessageGenerator.setPaymentErrorMode(false);
    }

    handleSubscribeFailure(response) {
        let errorMessage = this.settings.deliverySaver.paymentSetupGenericError;
        if (response.status === 400 && response.data.Errors) {
            const errorCodes = response.data.Errors.map(error => error.ErrorCode);

            if (errorCodes.includes(this.settings.digitalPayErrorCodes.creditCardStepUpTokenRequired)) {
                const stepUpTokenError = response.data.Errors.find(x => x.ErrorCode === this.settings.digitalPayErrorCodes.creditCardStepUpTokenRequired);
                this.showCvvConfirmationDialog(stepUpTokenError);
            } else if (errorCodes.includes(this.settings.subscriptionsErrorCodes.alreadySubscribed)) {
                errorMessage = this.settings.deliverySaver.paymentSetupAlreadySubscribedError;
            } else if (errorCodes.includes(this.settings.subscriptionsErrorCodes.planNotFound)) {
                errorMessage = this.settings.deliverySaver.paymentSetupPlanNotFoundError;
            }
        } else if (response.status === 504) {
            errorMessage = this.settings.deliverySaver.paymentSetupTimeoutError;
        } else if (response.status >= 500) {
            errorMessage = this.settings.deliverySaver.paymentSetupServerError;
        }
        this.digitalPayPaymentErrorDisplayService.pushGlobalError(errorMessage);
        this.shouldRemoveNewlyAddedInstruments = true;
    }

    showCvvConfirmationDialog(error) {
        this.cvvUrl = error.ErrorData.stepUpUrl;
        this.isCvvDialogEnabled = true;

        this.cvvConfirmationDialog.show().then((dialogResponse) => {
            if (dialogResponse.success) {
                this.paymentStateDefinitions.addDisablePaymentState(SUBSCRIPTION_PROCESSING_STATE_ID, this.settings.digitalPayScreenReaderMessages.subscriptionProcessingDisableMessage);
                this.resetPaymentStates();
                this.loadingIndicatorService.startLoading(true, 'Processing your payment', false);

                this.subscribePromise({
                    paymentInstrumentId: error.ErrorData.paymentInstrumentId,
                    stepUpToken: dialogResponse.stepUpToken,
                }).finally(() => {
                    this.loadingIndicatorService.finishLoading();
                    this.paymentStateDefinitions.removeDisablePaymentState(SUBSCRIPTION_PROCESSING_STATE_ID);
                });
            } else {
                this.digitalPayPaymentErrorDisplayService.pushGlobalError(dialogResponse.ErrorMessage);
            }
        }).finally(() => {
            this.isCvvDialogEnabled = false;
        });
    }

    closedCvvConfirmationDialog() {
        this.shouldRemoveNewlyAddedInstruments = true;
    }

    onInnerLinkClicked(event) {
        if (event.target.nodeName.toLowerCase() === 'a') {
            this.analytics.push({
                data: {
                    event: {
                        category: 'Delivery Unlimited',
                        label: `DSS: ${event.target.innerText}`,
                        name: 'link_click',
                    },
                    trackingType: 'link',
                },
                type: this.analytics.mapperTypes.customEvent.customEvent,
            });
        }
    }
}

DeliverySaverPaymentSetupController.$inject = [
    '$element',
    '$q',
    '$state',
    '$filter',
    '$scope',
    'Settings',
    'AnnouncerService',
    'DigitalPayPaymentErrorDisplayService',
    'LoadingIndicatorService',
    'DigitalPayIframeEventHandler',
    'DigitalPayPaymentDistributionService',
    'DigitalPayPrepaymentActionsService',
    'DigitalPayDisablePaymentStateService',
    'DigitalPayPaymentErrorStateService',
    'DigitalPayErrorMessageGenerator',
    'DeliverySaverService',
    'ShopperService',
    'DialogRegistry',
    'AnalyticsService',
];

const DeliverySaverPaymentSetupComponentDefinition = {
    controller: DeliverySaverPaymentSetupController,
    template: require('./delivery-saver-payment-setup.component.html').trim(),
};

module.exports = {
    DeliverySaverPaymentSetupController,
    DeliverySaverPaymentSetupComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/deliverysaver/paymentsetup/delivery-saver-payment-setup.component.js